import { Injectable, OnDestroy } from '@angular/core';

import { select, Store } from '@ngrx/store';
import { combineLatest, map, Observable, Subscription, take } from 'rxjs';

import {
  GA4Affiliation,
  Ga4AncillaryType,
  GA4Category,
  GA4Product,
  GA4PurchaseData,
  GA4PurchaseEvent,
  Ga4UpgradeWaitlist,
  GtmEvent,
  TripType,
} from '@fcom/common';
import { AppState } from '@fcom/core';
import { LanguageService } from '@fcom/ui-translate';
import { getTripTypeFromBookingLocationCodes } from '@fcom/common-booking/utils/common-booking.utils';
import { GtmService } from '@fcom/common/gtm';
import { TzDate } from '@fcom/core/utils';
import { CmsTripTypeMap } from '@fcom/core/utils/tripType.utils';

import { getBasketFlights, getBasketPassengerIds, getFlights, getUser, selectDataState } from '../store';
import { UpgradeAnalyticsLegacyService } from './upgrade-analytics-legacy.service';
import { Buyer, FlightsV2 } from '../api/models';
import { calculatePaymentTotal, getFlightsAsBookingLocationCodes } from '../utils';
import { ExtendedFlightBasket, FlightBasket } from '../interfaces';
import { getUpgradeFromFlight } from '../utils/data.utils';

@Injectable({
  providedIn: 'root',
})
export class UpgradeAnalyticsService implements OnDestroy {
  subscriptions: Subscription = new Subscription();
  dateFormat = 'yyyy-MM-dd';

  constructor(
    private upgradeAnalyticsLegacyService: UpgradeAnalyticsLegacyService,
    private store$: Store<AppState>,
    private languageService: LanguageService,
    private gtmService: GtmService
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  sendAddRemoveEvent(gtmEvent: GtmEvent.ADD_TO_CART | GtmEvent.REMOVE_FROM_CART, flightBasket: FlightBasket): void {
    this.subscriptions.add(
      combineLatest([
        this.store$.pipe(select(getFlights)),
        this.store$.pipe(select(getBasketPassengerIds)),
        this.store$.pipe(select(getUser)),
      ])
        .pipe(take(1))
        .subscribe(([flights, basketPassengers, buyer]) => {
          const fullFlight = flights.find((flight) => flight.segmentInfo.flightId === flightBasket.flightId);

          this.gtmService.ecommerceEventGA4(
            gtmEvent,
            this.mapUpgradestoGAProduct(
              [
                {
                  ...flightBasket,
                  flight: fullFlight,
                },
              ],
              basketPassengers,
              buyer
            )
          );
        })
    );
  }

  sendViewCartEvent(): void {
    this.subscriptions.add(
      this.getGa4PurchaseData(GtmEvent.VIEW_CART)
        .pipe(take(1))
        .subscribe((ecomNew) => {
          this.gtmService.ecommerceEventGA4(GtmEvent.VIEW_CART, ecomNew.items);
        })
    );
  }

  sendInitiateCheckoutEvent(): void {
    this.subscriptions.add(
      this.getGa4PurchaseData(GtmEvent.BEGIN_CHECKOUT)
        .pipe(take(1))
        .subscribe((ecomNew) => {
          this.gtmService.ecommerceEventGA4(GtmEvent.BEGIN_CHECKOUT, ecomNew.items);
        })
    );
  }

  sendPurchaseEvent(): void {
    this.upgradeAnalyticsLegacyService.sendUpgradePurchaseEvent();

    this.subscriptions.add(
      combineLatest([
        this.store$.pipe(select(getBasketFlights)),
        this.store$.pipe(select(getBasketPassengerIds)),
        this.store$.pipe(select(getUser)),
        this.languageService.translate('date'),
        this.getGa4PurchaseData(GtmEvent.PURCHASE),
        this.store$.pipe(select(getFlights)),
      ])
        .pipe(take(1))
        .subscribe(([basketFlights, basketPassengers, buyer, _dateTranslations, ecomNew, flights]) => {
          const paymentTotal = calculatePaymentTotal(basketFlights, basketPassengers.length, buyer);
          const allLocations = (flights as FlightsV2)
            .map(({ segmentInfo }) => `${segmentInfo.boardPoint}-${segmentInfo.offPoint}`)
            .join('_');
          const transactionEvent: GA4PurchaseEvent = {
            event: GtmEvent.PURCHASE,
            ...(paymentTotal.points ? { points_used: 'true' } : {}),
            ...(paymentTotal.points ? { points: paymentTotal.points } : {}),
            ecomNew,
            all_locations: allLocations,
          };

          this.gtmService.ecommercePurchaseEventGa4(transactionEvent);
        })
    );
  }

  getTransactionId(flights: FlightsV2, gtmEvent: GtmEvent): string {
    const bookingLocationCodes = getFlightsAsBookingLocationCodes(flights);
    const tripType = getTripTypeFromBookingLocationCodes(bookingLocationCodes);
    const dateTime = TzDate.now().toISOString();
    const locationCodesAsString = (tripType === TripType.RETURN ? [bookingLocationCodes[0]] : bookingLocationCodes)
      .map((locationCodes) => {
        return `${locationCodes.originLocationCode}-${locationCodes.destinationLocationCode}`;
      })
      .join(',');

    return `${gtmEvent}-${CmsTripTypeMap[tripType]}-${locationCodesAsString}-${dateTime}`;
  }

  getGa4PurchaseData(gtmEvent: GtmEvent): Observable<GA4PurchaseData> {
    return combineLatest([
      this.store$.pipe(select(getBasketFlights)),
      this.store$.pipe(select(getBasketPassengerIds)),
      this.store$.pipe(select(selectDataState)),
      this.store$.pipe(select(getUser)),
      this.languageService.translate('date'),
    ]).pipe(
      take(1),
      map(([basketFlights, basketPassengers, dataState, buyer, _dateTranslations]) => {
        const paymentTotal = calculatePaymentTotal(basketFlights, basketPassengers.length, buyer);

        return {
          transaction_id: this.getTransactionId(dataState.flights, gtmEvent),
          affiliation: GA4Affiliation.MMB,
          value: paymentTotal.money,
          tax: 0,
          shipping: 0,
          currency: paymentTotal.currency || 'EUR',
          items: this.mapUpgradestoGAProduct(basketFlights, basketPassengers, buyer),
        };
      })
    );
  }

  mapUpgradestoGAProduct(
    basketFlights: ExtendedFlightBasket[],
    basketPassengers: string[],
    buyer: Buyer
  ): GA4Product[] {
    return basketFlights.map((basketFlight) => {
      const originCode = basketFlight.flight.segmentInfo.boardPoint;
      const destinationCode = basketFlight.flight.segmentInfo.offPoint;
      const paymentInfo = calculatePaymentTotal(basketFlights, basketPassengers.length, buyer);
      const product = getUpgradeFromFlight(basketFlight.flight, basketFlight.productName);

      return {
        item_id: `Upgrade_${basketFlight.productName}`,
        item_name: `Upgrade_${basketFlight.productName}`,
        price: paymentInfo.money,
        quantity: basketPassengers.length,
        currency: paymentInfo.currency || 'EUR',
        affiliation: GA4Affiliation.MMB,
        item_category: GA4Category.ANCILLARIES,
        item_category2: Ga4AncillaryType.UPGRADE,
        item_category3: basketFlight.paymentType,
        item_category4: `${originCode}-${destinationCode}`,
        item_category5: product.isWaitlist ? Ga4UpgradeWaitlist.WAITLISTED : Ga4UpgradeWaitlist.NOT_WAITLISTED,
        item_variant: basketFlight.productName,
      };
    });
  }
}
